import React, { useState } from 'react';
import { Table, Button, Form, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../../shared/CurrencyContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../shared/api';
import { useConfirm } from '../../shared/ConfirmContext';
import { IconPlus } from '@tabler/icons-react';
import PageShell from '../../components/layout/PageShell';
import PageSection from '../../components/layout/PageSection';
import AppButton from '../../components/ui/AppButton';

const CurrencySettings = () => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const { refreshCurrencies, loading: contextLoading } = useCurrency();
  const [showModal, setShowModal] = useState(false);
  const [editingCurrency, setEditingCurrency] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [formData, setFormData] = useState({
    code: '',
    name: '',
    symbol: '',
    exchange_rate: '1.0',
    is_active: true
  });

  const { data: localCurrencies = [] } = useQuery({
    queryKey: ['currencies-settings'],
    queryFn: () => api.get('settings/api/currency/').then(d => Array.isArray(d) ? d : []),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['currencies-settings'] });
    refreshCurrencies();
  };

  const saveMutation = useMutation({
    mutationFn: (payload) => editingCurrency
      ? api.put(`settings/api/currency/${editingCurrency.id}/`, payload)
      : api.post('settings/api/currency/', payload),
    onSuccess: () => {
      setSuccess(editingCurrency ? 'Currency updated successfully' : 'Currency added successfully');
      setShowModal(false);
      invalidate();
    },
    onError: (err) => setError(err.message),
  });

  const defaultMutation = useMutation({
    mutationFn: (currencyId) => api.post(`settings/api/currency/${currencyId}/set_default/`),
    onSuccess: () => { setSuccess('Default currency updated successfully'); invalidate(); },
    onError: (err) => setError(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (currencyId) => api.delete(`settings/api/currency/${currencyId}/`),
    onSuccess: () => { setSuccess('Currency deleted successfully'); invalidate(); },
    onError: (err) => setError(err.message),
  });

  const loading = saveMutation.isPending || defaultMutation.isPending || deleteMutation.isPending;

  const handleEdit = (currency) => {
    setEditingCurrency(currency);
    setFormData({
      code: currency.code,
      name: currency.name,
      symbol: currency.symbol,
      exchange_rate: currency.exchange_rate,
      is_active: currency.is_active
    });
    setShowModal(true);
  };

  const handleAdd = () => {
    setEditingCurrency(null);
    setFormData({
      code: '',
      name: '',
      symbol: '',
      exchange_rate: '1.0',
      is_active: true
    });
    setShowModal(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    saveMutation.mutate(formData);
  };

  const handleSetDefault = (currencyId) => {
    setError(null);
    setSuccess(null);
    defaultMutation.mutate(currencyId);
  };

  const handleDelete = async (currencyId) => {
    if (!await confirm(t('settings.currency.deleteConfirm') || 'Are you sure you want to delete this currency?')) return;
    setError(null);
    setSuccess(null);
    deleteMutation.mutate(currencyId);
  };

  if (contextLoading && localCurrencies.length === 0) {
    return (
      <PageShell pretitle={t('settings.title')} title={t('settings.currency.title')}>
        <PageSection><div className="text-center p-4"><div className="spinner-border spinner-border-sm" /></div></PageSection>
      </PageShell>
    );
  }

  return (
    <PageShell
      pretitle={t('settings.title')}
      title={t('settings.currency.title')}
      actions={
        <AppButton variant="primary" icon={<IconPlus size={16} />} onClick={handleAdd}>
          {t('settings.currency.add')}
        </AppButton>
      }
    >
      {error && <div className="alert alert-danger app-inline-alert" role="alert">{error}</div>}
      {success && <div className="alert alert-success app-inline-alert" role="alert">{success}</div>}

      <PageSection title={t('settings.currency.title')} bodyClassName="p-0">
                    <Table responsive hover className="mb-0">
                      <thead>
                        <tr>
                          <th>{t('settings.currency.code')}</th>
                          <th>{t('settings.currency.name')}</th>
                          <th>{t('settings.currency.symbol')}</th>
                          <th>{t('settings.currency.exchangeRate')}</th>
                          <th>Status</th>
                          <th className="text-end">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {localCurrencies.map(currency => (
                          <tr key={currency.id}>
                            <td>
                              <strong>{currency.code}</strong>
                              {currency.is_default && (
                                <span className="badge bg-primary-lt ms-2">
                                  {t('settings.currency.default')}
                                </span>
                              )}
                            </td>
                            <td>{currency.name}</td>
                            <td><span className="h4 mb-0">{currency.symbol}</span></td>
                            <td>{currency.exchange_rate}</td>
                            <td>
                              {currency.is_active ? (
                                <span className="badge bg-success-lt">{t('settings.currency.active')}</span>
                              ) : (
                                <span className="badge bg-secondary-lt">{t('settings.currency.inactive')}</span>
                              )}
                            </td>
                            <td className="text-end">
                              <div className="btn-group">
                                {!currency.is_default && (
                                  <Button
                                    size="sm"
                                    variant="outline-primary"
                                    onClick={() => handleSetDefault(currency.id)}
                                    disabled={loading}
                                  >
                                    {t('settings.currency.setAsDefault')}
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="outline-info"
                                  onClick={() => handleEdit(currency)}
                                  disabled={loading}
                                  title={t('common.edit')}
                                >
                                  <i className="ti ti-edit"></i>
                                </Button>
                                {!currency.is_default && (
                                  <Button
                                    size="sm"
                                    variant="outline-danger"
                                    onClick={() => handleDelete(currency.id)}
                                    disabled={loading}
                                    title={t('common.delete')}
                                  >
                                    <i className="ti ti-trash"></i>
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
      </PageSection>

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingCurrency ? t('settings.currency.edit') : t('settings.currency.add')}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>{t('settings.currency.code')} *</Form.Label>
              <Form.Control
                type="text"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                placeholder={t('settings.currency.codePlaceholder')}
                maxLength={3}
                required
                disabled={editingCurrency !== null}
              />
              <Form.Text className="text-muted">
                {t('settings.currency.codePlaceholder')}
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('settings.currency.name')} *</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('settings.currency.namePlaceholder')}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('settings.currency.symbol')} *</Form.Label>
              <Form.Control
                type="text"
                value={formData.symbol}
                onChange={(e) => setFormData({ ...formData, symbol: e.target.value })}
                placeholder={t('settings.currency.symbolPlaceholder')}
                maxLength={10}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('settings.currency.exchangeRate')} *</Form.Label>
              <Form.Control
                type="number"
                step="any"
                value={formData.exchange_rate}
                onChange={(e) => setFormData({ ...formData, exchange_rate: e.target.value })}
                required
              />
              <Form.Text className="text-muted">
                {t('settings.currency.exchangeRateHelp')}
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label={t('settings.currency.isActive')}
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              {t('common.cancel')}
            </Button>
            <AppButton variant="primary" type="submit" loading={loading} loadingText={t('common.saving')}>
              {t('common.save')}
            </AppButton>
          </Modal.Footer>
        </Form>
      </Modal>
    </PageShell>
  );
};

export default CurrencySettings;
